// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
/**
 * The list of finished pages.
 *
 * Deliberately in-memory: pages are gone when the tab closes, and no copy is
 * written to IndexedDB, OPFS or localStorage on the way. Object URLs are
 * revoked as soon as a page leaves the list so the blobs can be collected.
 */

import type { ScanPage } from './types';

export class PageStore {
  private pages: ScanPage[] = [];

  get all(): readonly ScanPage[] {
    return this.pages;
  }

  get count(): number {
    return this.pages.length;
  }

  get isEmpty(): boolean {
    return this.pages.length === 0;
  }

  get(id: string): ScanPage | undefined {
    return this.pages.find((page) => page.id === id);
  }

  add(page: ScanPage): void {
    this.pages.push(page);
  }

  remove(id: string): boolean {
    const index = this.pages.findIndex((page) => page.id === id);
    if (index === -1) return false;
    URL.revokeObjectURL(this.pages[index].url);
    this.pages.splice(index, 1);
    return true;
  }

  /** Moves a page by `delta` positions, clamped to the ends. */
  move(id: string, delta: number): boolean {
    const index = this.pages.findIndex((page) => page.id === id);
    if (index === -1) return false;

    const target = Math.min(Math.max(index + delta, 0), this.pages.length - 1);
    if (target === index) return false;

    const [page] = this.pages.splice(index, 1);
    this.pages.splice(target, 0, page);
    return true;
  }

  clear(): void {
    for (const page of this.pages) URL.revokeObjectURL(page.url);
    this.pages = [];
  }

  get totalBytes(): number {
    return this.pages.reduce((sum, page) => sum + page.blob.size, 0);
  }
}
